import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import AHDateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import DocumentNameSchema from "documents/names/_schema/document-name-schema";
import DocumentTopicSchema from "documents/topics/_schema/document-topic-schema";
import { DocumentFileType } from "documents/types/document-file-type";
import { WorkDocumentType } from "work/work-agreements/contracts/types";
import { z } from "zod";

export type WorkDocumentJSON = z.infer<typeof WorkDocumentSchema.WorkDocumentJSONZ>;
export type WorkDocumentParams = z.infer<typeof WorkDocumentSchema.WorkDocumentZ>;

const DocumentFileTypeValues = Object.values(DocumentFileType) as [
  DocumentFileType,
  ...DocumentFileType[]
];

export const DocumentFileTypeSchema = z.enum(DocumentFileTypeValues);

export default class WorkDocumentSchema {
  public static readonly WorkDocumentJSONZ = z.object({
    id: z.uuid(),
    creatorId: z.uuid().nullish(),
    fileSize: z.number().nullish(),
    fileType: DocumentFileTypeSchema,
    created: z.iso.datetime({ offset: true }).nullish(),
    readAccess: z.array(z.uuid()).nullish(),
    writeAccess: z.array(z.uuid()).nullish(),
    hasSystemPermissions: z.boolean().nullish(),
    name: z.string(),
    topics: z.array(DocumentTopicSchema.DocumentTopicJSONZ).nullish(),
    templateIds: z.array(z.uuid()).nullish(),
    documentType: z.enum(WorkDocumentType).nullish(),
    replacesId: z.uuid().nullish(),
  });
  public static readonly WorkDocumentZ = z.object({
    id: z.instanceof(Guid),
    creatorId: z.instanceof(Guid).optional(),
    fileSize: z.number().optional(),
    fileType: DocumentFileTypeSchema,
    created: AHDateSchema.type.optional(),
    readAccess: z.array(z.instanceof(Guid)).optional(),
    writeAccess: z.array(z.instanceof(Guid)).optional(),
    hasSystemPermissions: z.boolean().optional(),
    name: DocumentNameSchema.DocumentNameZ,
    topics: z.array(DocumentTopicSchema.DocumentTopicZ).optional(),
    templateIds: z.array(z.instanceof(Guid)).optional(),
    replacesId: z.instanceof(Guid).optional(),
    documentType: z.enum(WorkDocumentType).optional(),
  });

  public static validate(values: unknown): WorkDocumentParams {
    return parseOrThrow(WorkDocumentSchema.WorkDocumentZ, values, {
      logError: true,
      schemaName: "WorkDocument",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): WorkDocumentJSON {
    return parseOrThrow(WorkDocumentSchema.WorkDocumentJSONZ, values, {
      logError: true,
      schemaName: "WorkDocument",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is WorkDocumentParams {
    try {
      WorkDocumentSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is WorkDocumentJSON {
    try {
      WorkDocumentSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: WorkDocumentJSON): WorkDocumentParams {
    const parsed = parseOrThrow(WorkDocumentSchema.WorkDocumentJSONZ, data, {
      logError: true,
      schemaName: "WorkDocument",
      operation: "deserialize",
      payloadSample: data,
    });
    return WorkDocumentSchema.codec.decode(parsed);
  }

  public static serialize(document: WorkDocumentParams): WorkDocumentJSON {
    const parsed = WorkDocumentSchema.validate(document);
    return WorkDocumentSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(WorkDocumentSchema.WorkDocumentJSONZ, WorkDocumentSchema.WorkDocumentZ, {
    decode: (data) => ({
      id: Guid.fromJSON(data.id)!,
      creatorId: Guid.fromJSON(data.creatorId) ?? undefined,
      fileSize: data.fileSize ?? undefined,
      fileType: data.fileType,
      created: AHDate.fromJSON(data.created) ?? undefined,
      readAccess: Guid.fromJSONArray(data.readAccess) ?? undefined,
      writeAccess: Guid.fromJSONArray(data.writeAccess) ?? undefined,
      hasSystemPermissions: Boolean(data.hasSystemPermissions ?? false),
      name: DocumentNameSchema.deserialize(data.name),
      topics: data.topics?.map(DocumentTopicSchema.deserialize),
      templateIds: Guid.fromJSONArray(data.templateIds) ?? undefined,
      documentType: WorkDocumentType[data.documentType as keyof typeof WorkDocumentType] ?? undefined,
      replacesId: Guid.fromJSON(data.replacesId) ?? undefined,
    }),
    encode: (document) => ({
      id: document.id.toJSON(),
      creatorId: document.creatorId?.toJSON(),
      fileSize: document.fileSize,
      fileType: document.fileType,
      created: document.created?.toJSON(),
      readAccess: document.readAccess?.map((guid) => guid.toJSON()),
      writeAccess: document.writeAccess?.map((guid) => guid.toJSON()),
      hasSystemPermissions: Boolean(document.hasSystemPermissions ?? false),
      name: DocumentNameSchema.serialize(document.name),
      topics: document.topics?.map(DocumentTopicSchema.serialize),
      templateIds: document.templateIds?.map((guid) => guid.toJSON()),
      documentType: document.documentType,
      replacesId: document.replacesId?.toJSON(),
    }),
  });
}
